import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Dashboard } from './components/Dashboard';
import { FloatingButton } from './components/FloatingButton';
import { JsonInputModal } from './components/JsonInputModal';
import { PasswordGate } from './components/PasswordGate';
import { useReport } from './hooks/useReport';
import { useS3Files } from './hooks/useS3Files';
import type { SalesAnalysisReport } from './types/report';

dayjs.extend(customParseFormat);

const App = () => {
  const { data: files, isLoading: isFilesLoading } = useS3Files();
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const {
    data: report,
    isLoading: isReportLoading,
    error,
  } = useReport(selectedFile);

  const isLoading = isFilesLoading || isReportLoading || !selectedFile;

  useEffect(() => {
    if (files && files.length > 0 && !selectedFile) {
      const sorted = [...files].sort().reverse();
      setSelectedFile(sorted[0]);
    }
  }, [files, selectedFile]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comparisonData, setComparisonData] =
    useState<SalesAnalysisReport | null>(null);

  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const isSyncing = useRef(false);

  const handleComparisonSubmit = (data: SalesAnalysisReport) => {
    setComparisonData(data);
  };

  const handleCloseComparison = () => {
    setComparisonData(null);
  };

  const handleScroll = useCallback((source: 'left' | 'right') => {
    if (isSyncing.current) return;

    const sourcePanel =
      source === 'left' ? leftPanelRef.current : rightPanelRef.current;
    const targetPanel =
      source === 'left' ? rightPanelRef.current : leftPanelRef.current;

    if (!sourcePanel || !targetPanel) return;

    isSyncing.current = true;
    // eslint-disable-next-line no-param-reassign
    targetPanel.scrollTop = sourcePanel.scrollTop;

    requestAnimationFrame(() => {
      isSyncing.current = false;
    });
  }, []);

  useEffect(() => {
    if (!comparisonData) return undefined;

    const leftPanel = leftPanelRef.current;
    const rightPanel = rightPanelRef.current;

    if (!leftPanel || !rightPanel) return undefined;

    const handleLeftScroll = () => handleScroll('left');
    const handleRightScroll = () => handleScroll('right');

    leftPanel.addEventListener('scroll', handleLeftScroll, { passive: true });
    rightPanel.addEventListener('scroll', handleRightScroll, { passive: true });

    return () => {
      leftPanel.removeEventListener('scroll', handleLeftScroll);
      rightPanel.removeEventListener('scroll', handleRightScroll);
    };
  }, [comparisonData, handleScroll]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-600 rounded-full animate-spin border-t-transparent" />
          <p className="text-gray-600">리포트 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error || (selectedFile && !report)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100">
        <div className="text-center">
          <p className="text-red-600">
            리포트 데이터를 불러오는데 실패했습니다.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            {error instanceof Error ? error.message : '알 수 없는 오류'}
          </p>
        </div>
      </div>
    );
  }

  const sortedFiles = files ? [...files].sort().reverse() : [];

  return (
    <PasswordGate>
      <div className="min-h-screen bg-slate-100">
        {/* Report Selector */}
        <div className="sticky top-0 z-20 bg-white border-b shadow-sm">
          <div className="flex items-center gap-3 px-4 py-3 mx-auto max-w-7xl">
            <span className="text-sm font-medium text-gray-700">
              리포트 선택
            </span>
            <select
              aria-label="리포트 선택"
              value={selectedFile || ''}
              onChange={(e) => setSelectedFile(e.target.value)}
              className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {sortedFiles.map((file) => (
                <option key={file} value={file}>
                  {dayjs(file.replace('.json', ''), 'YYYY-MM').format(
                    'YY년 MM월 매출'
                  )}
                </option>
              ))}
            </select>
          </div>
        </div>

        {comparisonData ? (
          // Comparison Mode: Split View
          <div className="flex h-screen">
            {/* Close button */}
            <button
              type="button"
              onClick={handleCloseComparison}
              className="fixed z-50 flex items-center gap-2 px-4 py-2 text-white bg-red-500 rounded-lg shadow-lg top-4 right-4 hover:bg-red-600"
            >
              <X className="w-4 h-4" />
              비교 종료
            </button>

            {/* Left Panel: Original Data */}
            <div
              ref={leftPanelRef}
              className="w-1/2 overflow-y-auto border-r-4 border-blue-500"
            >
              <div className="sticky top-0 z-10 px-4 py-2 font-semibold text-center text-white bg-blue-600">
                기존 데이터
              </div>
              <div className="py-8">
                <Dashboard report={report!} />
              </div>
            </div>

            {/* Right Panel: New Data */}
            <div ref={rightPanelRef} className="w-1/2 overflow-y-auto">
              <div className="sticky top-0 z-10 px-4 py-2 font-semibold text-center text-white bg-green-600">
                비교 데이터
              </div>
              <div className="py-8">
                <Dashboard report={comparisonData} />
              </div>
            </div>
          </div>
        ) : (
          // Normal Mode: Single View
          <div className="py-8">
            <Dashboard report={report!} />
          </div>
        )}

        {/* Floating Button (only visible in normal mode) */}
        {!comparisonData && (
          <FloatingButton onClick={() => setIsModalOpen(true)} />
        )}

        {/* JSON Input Modal */}
        <JsonInputModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleComparisonSubmit}
        />
      </div>
    </PasswordGate>
  );
};

export default App;
